import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES, type PurchaseOrderStatus } from './purchaseOrder';

/**
 *   perform('receive') ─────────────────────────────▶ allow (every status)
 *   clear ──▶ draft row? ───────────────────────────▶ allow
 *   else  ──▶ PO window ──▶ lot used ──▶ op / field rule
 *               DRAFT..RECEIVING       pick: sold through && SUBMITTED
 *                                      source: never
 *                                      unitsPicked · buyPrice: no open payable
 */
export type PurchaseOrderLineItemFacts = {
  purchaseOrderStatus: PurchaseOrderStatus;
  isSoldThrough: boolean;
  /** An OPEN or PAID_PARTIAL payable bills this order; an unknown bill set counts as open. */
  hasOpenPayable: boolean;
  /** The lot this line created has been drawn from. */
  lotUsed: boolean;
  isDraft: boolean;
};

export const PURCHASE_ORDER_LINE_ITEM_FIELDS = [
  'product',
  'external',
  'buyPrice',
  'unitsOrdered',
  'weightOrdered',
  'enteredByWeight',
  'unitsReceived',
  'weightReceived',
  'lotGrade',
  'unitsPicked',
  'weightPicked',
  'source',
  'palletTagsOnLineItem',
] as const;

export type PurchaseOrderLineItemField = (typeof PURCHASE_ORDER_LINE_ITEM_FIELDS)[number];

export const PURCHASE_ORDER_LINE_ITEM_OPS = ['clear', 'pick', 'receive'] as const;
export type PurchaseOrderLineItemOp = (typeof PURCHASE_ORDER_LINE_ITEM_OPS)[number];

/** The fields `perform('receive')` writes — receiving quantities and the grade they land at. */
export const PURCHASE_ORDER_LINE_ITEM_RECEIVING_FIELDS = [
  'unitsReceived',
  'weightReceived',
  'lotGrade',
] as const satisfies readonly PurchaseOrderLineItemField[];

type LineAction = Action<PurchaseOrderLineItemField, PurchaseOrderLineItemOp>;

const actionNotApplicable = (): Decision =>
  deny('PURCHASE_ORDER_LINE_ITEM_ACTION_NOT_APPLICABLE', 'Not available for this product.');

const decidePick = (facts: PurchaseOrderLineItemFacts): Decision =>
  facts.isSoldThrough && facts.purchaseOrderStatus === 'SUBMITTED'
    ? allow()
    : deny('PURCHASE_ORDER_LINE_ITEM_PICK_LOCKED', 'Picking is available only while the order is Submitted.');

const payableLock = (facts: PurchaseOrderLineItemFacts): Decision =>
  facts.hasOpenPayable ? deny('PURCHASE_ORDER_LINE_ITEM_PAYABLE_LOCKED', 'Locked while the payable is open.') : allow();

const decideField = (facts: PurchaseOrderLineItemFacts, field: PurchaseOrderLineItemField): Decision => {
  switch (field) {
    case 'source':
      return deny('PURCHASE_ORDER_LINE_ITEM_READ_ONLY_FIELD', 'Source is set by the system.');
    case 'buyPrice':
      return payableLock(facts);
    case 'unitsPicked': {
      const locked = payableLock(facts);
      return locked.allowed ? decidePick(facts) : locked;
    }
    case 'weightPicked':
      return decidePick(facts);
    case 'product':
    case 'external':
    case 'unitsOrdered':
    case 'weightOrdered':
    case 'enteredByWeight':
    case 'unitsReceived':
    case 'weightReceived':
    case 'lotGrade':
    case 'palletTagsOnLineItem':
      return allow();
  }
};

const decideEntity = (facts: PurchaseOrderLineItemFacts, action: LineAction): Decision => {
  if (action.type === 'view') return allow();
  if (action.type === 'perform' && action.op === 'receive') return allow();
  if (action.type === 'perform' && action.op === 'clear') return facts.isDraft ? allow() : actionNotApplicable();

  if (!PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES.includes(facts.purchaseOrderStatus)) {
    return deny(
      'PURCHASE_ORDER_LINE_ITEMS_LOCKED',
      `Products are locked once the purchase order is ${getStatusLabel(facts.purchaseOrderStatus)}.`
    );
  }

  if (action.type === 'add') return allow();

  if (facts.lotUsed) {
    return deny('PURCHASE_ORDER_LINE_ITEM_LOT_USED', "Can't remove: this product's lot is already in use.");
  }

  if (action.type === 'delete') return facts.isDraft ? actionNotApplicable() : allow();
  if (action.type === 'perform') return decidePick(facts);

  return allow();
};

const fieldRule = (facts: PurchaseOrderLineItemFacts, action: LineAction): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  product: fieldRule,
  external: fieldRule,
  buyPrice: fieldRule,
  unitsOrdered: fieldRule,
  weightOrdered: fieldRule,
  enteredByWeight: fieldRule,
  unitsReceived: fieldRule,
  weightReceived: fieldRule,
  lotGrade: fieldRule,
  unitsPicked: fieldRule,
  weightPicked: fieldRule,
  source: fieldRule,
  palletTagsOnLineItem: fieldRule,
} satisfies Record<PurchaseOrderLineItemField, typeof fieldRule>;

export const purchaseOrderLineItemPolicy: Policy<
  PurchaseOrderLineItemFacts,
  PurchaseOrderLineItemField,
  PurchaseOrderLineItemOp
> = definePolicy<PurchaseOrderLineItemFacts, PurchaseOrderLineItemField, PurchaseOrderLineItemOp>({
  kind: 'purchase order product',
  fields: PURCHASE_ORDER_LINE_ITEM_FIELDS,
  ops: PURCHASE_ORDER_LINE_ITEM_OPS,
  requires: {},
  entity: decideEntity,
  field: fieldRules,
});
